'use client';

import { type MouseEventHandler, forwardRef, useCallback, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { CloseButton } from '../close-button/close-button';
import { Slot } from '../slot';
import { ModalRootContext } from './context/modal-root.context';
import { modalCloseButtonStyles } from './styles/modal-close-button.styles';
import type { ModalCloseButtonPropsInterface } from './types/modal-close-button.types';

/**
 * @since 10.0.0
 */
export const ModalCloseButton = forwardRef<HTMLButtonElement, ModalCloseButtonPropsInterface>(
  (props: ModalCloseButtonPropsInterface, forwardedRef) => {
    const { children = <CloseButton />, className, css: cssProp, onClick, ...rest } = props;
    const { modal } = useContext(ModalRootContext);

    const onClickHandler = useCallback<MouseEventHandler<HTMLButtonElement>>(
      (event) => {
        modal.onClickClose(event);
        onClick?.(event);
      },
      [modal, onClick],
    );
    const isNativeElement = typeof children.type === 'string';

    return (
      <Slot
        ref={forwardedRef}
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Modal.CloseButton"
        {...(isNativeElement
          ? {
              className: cx(
                'c11n_modal__close-button',
                css(modalCloseButtonStyles, cssProp),
                className,
              ),
            }
          : {
              css: { ...modalCloseButtonStyles, ...cssProp },
              className: cx('c11n_modal__close-button', className),
            })}
        {...rest}
        onClick={onClickHandler}
      >
        {children}
      </Slot>
    );
  },
);

ModalCloseButton.displayName = 'Modal.CloseButton';
